import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Mail, ArrowLeft, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
      toast.success("If an account exists for that email, a reset link is on its way.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid-bg min-h-[calc(100vh-73px)] flex items-center">
      <div className="max-w-md w-full mx-auto px-6 py-16">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white mb-6"
          data-testid="back-to-login-link"
        >
          <ArrowLeft size={14} /> Back to login
        </Link>

        {sent ? (
          <div className="card-surface p-8 text-center" data-testid="forgot-password-sent">
            <CheckCircle2 className="mx-auto text-[#22C55E] mb-4" size={48} />
            <h1 className="font-display font-extrabold text-3xl tracking-tighter mb-2">
              Check your inbox
            </h1>
            <p className="text-zinc-400 text-sm mb-6">
              If an account exists for <span className="text-white font-mono-nums">{email}</span>,
              we just sent a reset link. It expires in <strong>1 hour</strong>.
            </p>
            <p className="text-zinc-500 text-xs">
              Don't see it? Check spam or wait a minute, then{" "}
              <button
                onClick={() => setSent(false)}
                className="text-[#FBBF24] hover:underline"
                data-testid="try-again-button"
              >
                try again
              </button>
              .
            </p>
          </div>
        ) : (
          <>
            <h1 className="font-display font-extrabold text-4xl tracking-tighter mb-2">
              Forgot password?
            </h1>
            <p className="text-zinc-500 text-sm mb-8">
              Enter the email on file and we'll send you a reset link.
            </p>
            <form onSubmit={submit} className="card-surface p-6 space-y-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-zinc-500 mb-2 block">
                  Email
                </label>
                <input
                  type="email"
                  required
                  className="input-field"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="forgot-email-input"
                  placeholder="you@example.com"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="btn-bid w-full text-sm flex items-center justify-center gap-2"
                data-testid="forgot-submit-button"
              >
                <Mail size={14} />
                {loading ? "Sending…" : "Send reset link"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
